// ── ADMIN: STUDENT BILLING ACTIONS ─────────────────────────────────────
// Modals reached from admin-students.js's performer detail page (real
// imports there now, wired via VIEW_INIT['a-performer-detail']): edit
// performer identity (childModal/saveChild), additive enrollment
// (enrollProgramModal family), mid-season billing switch
// (billingSwitchModal/processBillingSwitch), and drop-from-program
// (dropModal family). Kept together because they were one contiguous
// section of legacy-monolith.js and share PC_DROP_FEES/currencyFocus/
// currencyBlur. Each modal function wires its own buttons directly inline
// right after openModal() — imperative functions with control over the
// modal DOM they just wrote, same pattern as billing-shared.js's cardModal.
//
// currencyFocus/currencyBlur are also a real import in admin-settings.js
// (the late-fee field), in addition to their use here — exported below.
//
// esc (shared classic script) stays a bare global — not migrated yet.
import { apiFetch } from '../core/api.js';
import { DB, PROGRAMS, session, currentView, GRADES } from '../core/state.js';
import { calcAge, TODAY } from '../core/format.js';
import { kidPrograms, activePrograms, currentSeasonYear, upcomingSeasonYear, priorityWindowOpen, isContinuingPerformer, enrollmentBillingMode, enrollmentMonthlyRate, activeEnrollment, isAdmin } from '../core/domain-utils.js';
import { openModal, closeModal, toast, toastError } from '../core/ui.js';
import { refresh } from '../core/routing.js';
import { priceLabel, programSeasonTotal, currentSemester, upcomingSemester } from './billing-shared.js';
import { renderStudentTable } from './admin-students.js';

// ── CURRENCY FIELD MASK ──────────────────────────────────────────────
// On focus, show the bare number for easy editing; on blur, format as $X.XX.
export function currencyFocus(input) {
  input.value = input.value.replace(/[^0-9.]/g, '');
}
export function currencyBlur(input) {
  const n = parseFloat(input.value.replace(/[^0-9.]/g, ''));
  input.value = '$' + (isNaN(n) ? 0 : n).toFixed(2);
}

// Edit an existing performer's identity only. Performers are created through the
// registration → contract → payment flow, never added manually here, so there is no
// create mode. Enrollments, billing, and drops are managed on the performer detail page.
export function childModal(childId) {
  const ch = DB.children.find(c => c.id === childId);
  if (!ch) return;
  const adminMode = isAdmin(session);
  openModal(`
    <h3>Edit performer</h3>
    <div class="field-row">
      <div class="field"><label>First name</label><input type="text" id="cmFirst" value="${esc(ch.firstName || '')}" placeholder="First name"></div>
      <div class="field"><label>Last name</label><input type="text" id="cmLast" value="${esc(ch.lastName || '')}" placeholder="Last name"></div>
    </div>
    <div class="field"><label>Date of birth</label><input type="date" id="cmDob" value="${esc(ch.dob || '')}"></div>
    <div class="field"><label>Gender</label>
      ${adminMode ? `
        <select id="cmGender">
          <option value="" ${ch.gender ? '' : 'selected'}>Select</option>
          <option value="Female" ${ch.gender === 'Female' ? 'selected' : ''}>Female</option>
          <option value="Male" ${ch.gender === 'Male' ? 'selected' : ''}>Male</option>
        </select>`
      : `<div style="padding:10px 13px;background:rgba(255,255,255,0.03);border:1px solid var(--border-soft);border-radius:7px;color:var(--muted);font-size:14px">${ch.gender ? esc(ch.gender) : 'Not specified'}</div>`}
    </div>
    <div class="field-row">
      <div class="field"><label>Grade <span class="plain-cap">(this fall)</span></label>
        <select id="cmGrade">
          <option value="">Select</option>
          ${GRADES.map(g => `<option value="${esc(g)}" ${ch.grade === g ? 'selected' : ''}>${esc(g)}</option>`).join('')}
        </select></div>
      <div class="field"><label>School <span class="plain-cap">(this fall)</span></label>
        <input type="text" id="cmSchool" data-school-input autocomplete="off" value="${esc(ch.school || '')}" placeholder="Start typing a school name"></div>
    </div>
    <div style="display:flex;gap:10px;margin-top:14px">
      <button class="btn" id="childSaveBtn" style="flex:1">Save changes</button>
      <button class="btn-ghost" style="flex:1" onclick="closeModal()">Cancel</button>
    </div>`);
  document.getElementById('childSaveBtn').addEventListener('click', () => saveChild(ch.id));
}

async function saveChild(childId) {
  const ch = DB.children.find(c => c.id === childId);
  if (!ch) return;
  const firstName = document.getElementById('cmFirst').value.trim();
  const lastName  = document.getElementById('cmLast').value.trim();
  const dob  = document.getElementById('cmDob').value;
  if (!firstName || !lastName || !dob) { toast('First name, last name, and date of birth are required'); return; }
  const genderEl = document.getElementById('cmGender');
  const grade  = document.getElementById('cmGrade') ? document.getElementById('cmGrade').value : '';
  const school = document.getElementById('cmSchool') ? document.getElementById('cmSchool').value.trim() : '';
  const payload = { firstName, lastName, dob, grade, school };
  if (genderEl) payload.gender = genderEl.value;
  try {
    const updated = await apiFetch('/performers/' + childId, { method: 'PUT', body: JSON.stringify(payload) });
    Object.assign(ch, updated, { name: updated.firstName + ' ' + updated.lastName });
    closeModal();
    if (currentView === 'a-students') renderStudentTable(); else refresh();
    toast('Performer updated');
  } catch (e) { toastError(e.message); }
}

// ── DROP FROM PROGRAM (admin only) ───────────────────────────────────
// Performance Company uses a fee schedule; Broadway Bound is a custom amount.
const PC_DROP_FEES = [
  { label: 'Waive cancellation fee', amount: 0 },
  { label: 'Scheduled semester break (December)', amount: 0 },
  { label: 'First semester — on or before Sep 30', amount: 150 },
  { label: 'First semester — on or before Oct 31', amount: 200 },
  { label: 'First semester — on or before Nov 30', amount: 250 },
  { label: 'December — ungraceful exit', amount: 300 },
  { label: 'Second semester — before cast list date', amount: 150 },
  { label: 'Second semester — on or before Jan 31', amount: 250 },
  { label: 'Second semester — on or before Feb 28', amount: 300 },
  { label: 'Second semester — before show closes (April)', amount: 400 }
];

// ── MID-SEASON BILLING SWITCH (admin) ────────────────────────────────
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
// Months of the current billing unit that are not yet paid (used for monthly→advance).
// For PC the unit is the current semester; for BB the unit is the whole season.
function remainingUnitMonths(p, accountId, performerId) {
  // Only meaningful while a billing unit is in progress. No current semester → no remaining unit.
  const sem = currentSemester(p, TODAY);
  if (!sem) return [];
  const unitMonths = sem.months;
  if (!unitMonths.length) return [];
  const curMonth = TODAY.getMonth() + 1;
  const paidMonths = new Set(
    DB.charges.filter(c => c.accountId === accountId && c.childId === performerId &&
      c.programId === p.id && c.type === 'Monthly')
      .map(c => new Date(c.date + 'T00:00').getMonth() + 1)
  );
  // Months an earlier advance charge already paid for (advance, switched to
  // monthly, now back to advance). Mirrors BillingRun::advanceCoverFor().
  // This season only (BillingRun::seasonOf: Sep-Dec is that year, Jan-Aug the year before).
  const season = TODAY.getMonth() >= 8 ? TODAY.getFullYear() : TODAY.getFullYear() - 1;
  const prepaid = DB.charges.filter(c => c.accountId === accountId && c.childId === performerId &&
    c.programId === p.id && c.type === 'Monthly' && c.status !== 'failed' && c.seasonYear === season);
  const coveredByAdvance = m => prepaid.some(c =>
    (c.desc || '').includes(` ${sem.label} (advance)`) ||
    ((c.desc || '').includes('(switched to advance)') && c.date &&
      unitMonths.indexOf(new Date(c.date + 'T00:00').getMonth() + 1) !== -1 &&
      unitMonths.indexOf(new Date(c.date + 'T00:00').getMonth() + 1) <= unitMonths.indexOf(m)));
  return unitMonths.filter(m => !paidMonths.has(m) && !coveredByAdvance(m) && monthIsCurrentOrLater(m, curMonth, unitMonths));
}
function monthIsCurrentOrLater(m, curMonth, unitMonths) {
  const idx = unitMonths.indexOf(m);
  const curIdx = unitMonths.indexOf(curMonth);
  if (curIdx === -1) return true;  // current month outside the unit: keep all unit months
  return idx >= curIdx;
}

// Explicit additive enrollment — admin enrolls a performer into a NEW eligible program.
// (Dropping is a separate, deliberate action; the two are never the same control.)
export function enrollProgramModal(childId) {
  const ch = DB.children.find(c => c.id === childId);
  if (!ch) return;
  const age = calcAge(ch.dob);
  const already = kidPrograms(ch);
  const options = activePrograms().filter(p =>
    !already.includes(p.id) &&
    !(age !== null && ((p.ageMin !== null && age < p.ageMin) || (p.ageMax !== null && age > p.ageMax)))
  );
  if (!options.length) { toast('No eligible programs to add'); return; }
  openModal(`
    <h3>Enroll ${esc(ch.firstName)} in a program</h3>
    <div class="field"><label>Program</label>
      <select id="epProgram">
        ${options.map(p => `<option value="${p.id}">${esc(p.name)} — ${priceLabel(p)}</option>`).join('')}
      </select>
    </div>
    <div id="epBillingRow"></div>
    ${priorityWindowOpen() && isContinuingPerformer(ch.id) ? `
    <div style="font-size:12px;line-height:1.55;color:var(--cream);background:rgba(201,168,76,0.08);border:1px solid rgba(201,168,76,0.35);border-radius:6px;padding:10px 12px;margin:4px 0 10px">
      Re-enrollment for ${seasonLabel(DB.settings.upcomingSeasonYear)} is open, and the family should do it themselves from their portal: they choose the program and how to pay, and the materials fee is charged in the same step. This button enrolls in the current ${seasonLabel(currentSeasonYear())} season.
    </div>` : ''}
    <p id="epNote" style="font-size:12px;color:var(--muted);margin:4px 0 16px"></p>
    <div style="display:flex;gap:10px">
      <button class="btn" id="epEnrollBtn" style="flex:1">Enroll</button>
      <button class="btn-ghost" style="flex:1" onclick="closeModal()">Cancel</button>
    </div>`, true);
  document.getElementById('epProgram').addEventListener('change', enrollProgramModalSync);
  document.getElementById('epEnrollBtn').addEventListener('click', () => confirmEnrollProgram(childId));
  enrollProgramModalSync();
}
// Show billing-mode choice for programs that allow advance; note one-time for classes.
function enrollProgramModalSync() {
  const sel = document.getElementById('epProgram');
  const p = PROGRAMS.find(x => x.id === (sel && sel.value));
  const row = document.getElementById('epBillingRow');
  const note = document.getElementById('epNote');
  if (!p || !row) return;
  if (p.isProgram && p.allowAdvance) {
    const adv = p.semesters && p.semesters.length > 1 ? `Pay by semester (${fmt$(p.monthly * (p.semesters[0].months || []).length)}/sem)` : `Pay full season (${fmt$(programSeasonTotal(p))})`;
    row.innerHTML = `<div class="field"><label>Billing</label>
      <select id="epBilling">
        <option value="monthly">Monthly (${fmt$(p.monthly)}/mo)</option>
        <option value="advance">${esc(adv)}</option>
      </select></div>`;
    note.textContent = 'Tuition begins per the billing choice. Any materials fee is billed once their contract is signed.';
  } else if (p.isProgram) {
    row.innerHTML = '';
    note.textContent = 'Billed monthly during the season. Any materials fee is billed once their contract is signed.';
  } else {
    row.innerHTML = '';
    note.textContent = 'One-time charge billed at enrollment.';
  }
}
async function confirmEnrollProgram(childId) {
  const ch = DB.children.find(c => c.id === childId);
  const sel = document.getElementById('epProgram');
  const p = PROGRAMS.find(x => x.id === (sel && sel.value));
  if (!ch || !p) { closeModal(); return; }
  const billingEl = document.getElementById('epBilling');
  const mode = p.isProgram ? (billingEl ? billingEl.value : 'monthly') : 'onetime';
  try {
    await apiFetch('/enrollments', {
      method: 'POST',
      body: JSON.stringify({ performerId: ch.id, programId: p.id, seasonYear: currentSeasonYear(), billingMode: mode })
    });
    if (!ch.programIds.includes(p.id)) ch.programIds.push(p.id);
    // Reload charges and enrollments to reflect new state
    DB.charges = await apiFetch('/charges');
    DB.seasonEnrollments = (await apiFetch('/enrollments')).map(se => ({
      id: se.id, performerId: se.performerId, programId: se.programId,
      seasonYear: se.seasonYear, status: se.status, billingMode: se.billingMode,
      rateOverride: se.rateOverride ?? null,
      registeredAt: se.createdAt, materialsFeePaid: false, contractSent: false, contractSigned: false
    }));
    closeModal(); refresh();
    toast(`${ch.firstName} enrolled in ${p.name}`);
  } catch (e) { toastError(e.message); }
}

export function billingSwitchModal(childId, programId) {
  const ch = DB.children.find(c => c.id === childId);
  const p = PROGRAMS.find(x => x.id === programId);
  if (!ch || !p) return;
  const mode = enrollmentBillingMode(childId, programId);
  // Every figure in this modal is what THIS performer will be charged, so it is
  // built from their enrollment rate, not the program's. The two are the same
  // for all but the rare reduced-tuition enrollment -- and for those, quoting
  // the program's rate here would overcharge on the switch.
  const rate = enrollmentMonthlyRate(childId, programId);
  const sem = currentSemester(p, TODAY) || (p.semesters && p.semesters[0]);
  const card = (() => { const a = DB.users.find(u => u.id === ch.accountId); return a && a.card ? `${esc(a.card.brand)} ending in ${esc(a.card.last4)}` : null; })();

  if (mode === 'monthly') {
    // A monthly→advance switch normally charges the remainder of the unit already underway.
    // Off-season there is no remainder to compute, but that does NOT mean the switch is
    // meaningless: if the enrollment is for the season about to start, flipping to advance now
    // charges nothing today and lets advance_billing.php bill the whole first unit at its
    // startMonth. That's exactly what a parent asking in August wants, in one step.
    const inProgress = currentSemester(p, TODAY);
    if (!inProgress) {
      const next = upcomingSemester(p, TODAY);
      const se = activeEnrollment(childId, programId);
      // Only for an enrollment in the season that's about to begin. A stale enrollment from a
      // finished season must never be flipped to advance — the advance cron doesn't filter by
      // season year, so it would bill the family again for a season they already completed.
      const preSeason = next && se && se.seasonYear === upcomingSeasonYear();
      if (!preSeason) {
        openModal(`
          <h3>Switch to pay-in-advance</h3>
          <p class="card-intro">${esc(ch.name)}'s ${esc(p.name)} season isn't currently in progress, so there's no in-progress ${p.semesters && p.semesters.length > 1 ? 'semester' : 'season'} to pay ahead on right now.</p>
          <p class="card-intro">Billing changes apply once the season is underway. To set a pay-in-advance preference for the upcoming season, use the family's re-enrollment.</p>
          <div style="display:flex;gap:10px">
            <button class="btn" style="flex:1" onclick="closeModal()">Got it</button>
          </div>`, true);
        return;
      }
      const multiUnit = p.semesters.length > 1;
      const unitWord = multiUnit ? 'semester' : 'season';
      const nextLabel = next.label || next.name || (multiUnit ? 'First semester' : 'Season');
      const nextTotal = rate * (next.months || []).length;
      const startName = MONTH_NAMES[(next.startMonth ?? next.months[0]) - 1];
      openModal(`
        <h3>Switch to pay-in-advance</h3>
        <p class="card-intro">Switch <b style="color:var(--cream)">${esc(ch.name)}</b>'s ${esc(p.name)} billing to pay-in-advance. The season hasn't started, so <b style="color:var(--cream)">nothing is charged today</b>.</p>
        <div style="background:rgba(255,255,255,0.03);border:1px solid var(--border-soft);border-radius:8px;padding:14px 16px;margin-bottom:16px;font-size:13px;line-height:1.9">
          <div style="display:flex;justify-content:space-between"><span style="color:var(--muted)">Charge now</span><span style="color:var(--cream)">${fmt$(0)}</span></div>
          <div style="display:flex;justify-content:space-between;border-top:1px solid var(--border-soft);margin-top:8px;padding-top:8px"><span style="color:var(--cream)">On ${esc(startName)} 1 — ${esc(nextLabel)}</span><span style="font-family:'Libre Baskerville',serif;font-size:16px;color:var(--cream)">${fmt$(nextTotal)}</span></div>
        </div>
        <p style="font-size:12px;color:var(--muted);margin-bottom:14px">The full ${unitWord} is billed automatically on ${esc(startName)} 1 — no monthly charges.${multiUnit ? ' Each later semester bills the same way at its start.' : ''}</p>
        ${card ? `<p style="font-size:12px;color:var(--muted);margin-bottom:14px">Will charge ${card}.</p>` : `<p style="font-size:12px;color:var(--red);margin-bottom:14px">No card on file — add one before ${esc(startName)} 1 or the charge will fail.</p>`}
        <div id="bswErr" style="display:none;font-size:12px;color:var(--red);margin-bottom:12px"></div>
        <div style="display:flex;gap:10px">
          <button class="btn" id="bswBtn" style="flex:1">Switch to advance</button>
          <button class="btn-ghost" style="flex:1" onclick="closeModal()">Cancel</button>
        </div>`, true);
      document.getElementById('bswBtn').addEventListener('click', () => processBillingSwitch(childId, programId, 'advance', 0));
      return;
    }
    const remaining = remainingUnitMonths(p, ch.accountId, ch.id);
    const charge = remaining.length * rate;
    const unitLabel = inProgress.label;
    openModal(`
      <h3>Switch to pay-in-advance</h3>
      <p class="card-intro">Switch <b style="color:var(--cream)">${esc(ch.name)}</b>'s ${esc(p.name)} billing to pay-in-advance.</p>
      <div style="background:rgba(255,255,255,0.03);border:1px solid var(--border-soft);border-radius:8px;padding:14px 16px;margin-bottom:16px;font-size:13px;line-height:1.9">
        <div style="display:flex;justify-content:space-between"><span style="color:var(--muted)">Remaining in ${esc(unitLabel)}</span><span style="color:var(--cream)">${remaining.length} ${remaining.length === 1 ? 'month' : 'months'} × ${fmt$(rate)}</span></div>
        <div style="display:flex;justify-content:space-between;border-top:1px solid var(--border-soft);margin-top:8px;padding-top:8px"><span style="color:var(--cream)">Charge now</span><span style="font-family:'Libre Baskerville',serif;font-size:16px;color:var(--cream)">${fmt$(charge)}</span></div>
      </div>
      ${p.semesters && p.semesters.length > 1 ? `<p style="font-size:12px;color:var(--muted);margin-bottom:14px">This covers the rest of the current semester. The next semester bills in advance automatically at its start.</p>` : `<p style="font-size:12px;color:var(--muted);margin-bottom:14px">This covers the remaining season.</p>`}
      ${card ? `<p style="font-size:12px;color:var(--muted);margin-bottom:14px">Charging ${card}.</p>` : `<p style="font-size:12px;color:var(--red);margin-bottom:14px">No card on file — add one before switching.</p>`}
      <div id="bswErr" style="display:none;font-size:12px;color:var(--red);margin-bottom:12px"></div>
      <div style="display:flex;gap:10px">
        <button class="btn" id="bswBtn" style="flex:1" ${card ? '' : 'disabled'}>${charge > 0 ? 'Charge ' + fmt$(charge) + ' & switch' : 'Switch to advance'}</button>
        <button class="btn-ghost" style="flex:1" onclick="closeModal()">Cancel</button>
      </div>`, true);
    document.getElementById('bswBtn').addEventListener('click', () => processBillingSwitch(childId, programId, 'advance', charge));
  } else {
    openModal(`
      <h3>Switch to monthly billing</h3>
      <p class="card-intro">Switch <b style="color:var(--cream)">${esc(ch.name)}</b>'s ${esc(p.name)} billing back to monthly. Any semester already paid in advance is not refunded; upcoming months not yet covered will bill monthly on the 1st.</p>
      <div style="display:flex;gap:10px">
        <button class="btn" id="bswMonthlyBtn" style="flex:1">Switch to monthly</button>
        <button class="btn-ghost" style="flex:1" onclick="closeModal()">Cancel</button>
      </div>`, true);
    document.getElementById('bswMonthlyBtn').addEventListener('click', () => processBillingSwitch(childId, programId, 'monthly', 0));
  }
}

async function processBillingSwitch(childId, programId, newMode, chargeAmount) {
  const ch = DB.children.find(c => c.id === childId);
  const p = PROGRAMS.find(x => x.id === programId);
  const se = activeEnrollment(childId, programId);
  if (!ch || !p || !se) { closeModal(); return; }

  // Every path here is an API call, including the $0 pre-season switch, so the button is always
  // disabled while it's in flight. Restoring the label from what was actually rendered keeps the
  // three entry points (remainder charge, $0 pre-season, back-to-monthly) from being second-guessed.
  const btn = document.getElementById('bswBtn') || document.getElementById('bswMonthlyBtn');
  const errEl = document.getElementById('bswErr');
  const originalLabel = btn ? btn.textContent : '';
  if (btn) { btn.disabled = true; btn.textContent = 'Processing…'; }

  try {
    await apiFetch('/enrollments/' + se.id + '/billing-switch', {
      method: 'POST', body: JSON.stringify({ billingMode: newMode, chargeAmount })
    });
    se.billingMode = newMode;
    if (chargeAmount > 0) DB.charges = await apiFetch('/charges');
    closeModal(); refresh();
    toast(`${ch.name}'s ${p.name} billing switched to ${newMode === 'advance' ? 'pay-in-advance' : 'monthly'}`);
  } catch (e) {
    if (btn) { btn.disabled = false; btn.textContent = originalLabel; }
    const message = e.message || (chargeAmount > 0
      ? 'The charge was declined. Billing was not switched.'
      : 'Billing was not switched. Please try again.');
    if (errEl) { errEl.textContent = message; errEl.style.display = 'block'; }
    else toastError(message);
  }
}

export function dropModal(childId, programId) {
  const ch = DB.children.find(c => c.id === childId);
  const p = PROGRAMS.find(x => x.id === programId);
  if (!ch || !p) return;
  const isPC = p.name && p.name.toLowerCase().includes('performance company');
  const existingDue = DB.charges.filter(c => c.accountId === ch.accountId && c.status === 'due').reduce((s, c) => s + c.amount, 0);
  const acct = DB.users.find(u => u.id === ch.accountId);
  const card = acct && acct.card ? `${esc(acct.card.brand)} ending in ${esc(acct.card.last4)}` : null;

  const feeControl = isPC
    ? `<div class="field"><label>Cancellation fee</label>
        <select id="dropFeeSelect">
          <option value="" selected disabled>Select a cancellation fee…</option>
          ${PC_DROP_FEES.map((f) => `<option value="${f.amount}">${esc(f.label)} — ${fmt$(f.amount)}</option>`).join('')}
        </select></div>`
    : `<div class="field"><label>Cancellation fee</label>
        <input type="text" id="dropFeeInput" value="" placeholder="$0.00" inputmode="decimal">
        <span style="font-size:11px;color:var(--muted);margin-top:4px">Broadway Bound has no set schedule — enter the agreed amount, or $0 to waive.</span></div>`;

  openModal(`
    <h3>Drop from ${esc(p.name)}</h3>
    <p class="card-intro">Dropping <b style="color:var(--cream)">${esc(ch.name)}</b> from ${esc(p.name)}. The account must be paid in full — including any cancellation fee — to complete the drop. The card on file will be charged for the full balance.</p>
    ${feeControl}
    <div style="background:rgba(255,255,255,0.03);border:1px solid var(--border-soft);border-radius:8px;padding:14px 16px;margin:16px 0;font-size:13px;line-height:1.9">
      <div style="display:flex;justify-content:space-between"><span style="color:var(--muted)">Existing balance due</span><span style="color:var(--cream)">${fmt$(existingDue)}</span></div>
      <div style="display:flex;justify-content:space-between"><span style="color:var(--muted)">Cancellation fee</span><span style="color:var(--cream)" id="dropFeeAmt">—</span></div>
      <div style="display:flex;justify-content:space-between;border-top:1px solid var(--border-soft);margin-top:8px;padding-top:8px"><span style="color:var(--cream)">Total to charge</span><span style="font-family:'Libre Baskerville',serif;font-size:16px;color:var(--cream)" id="dropTotalAmt">${fmt$(existingDue)}</span></div>
    </div>
    ${card ? `<p style="font-size:12px;color:var(--muted);margin-bottom:14px">Charging ${card}.</p>`
           : `<p style="font-size:12px;color:var(--red);margin-bottom:14px">No card on file — add one before dropping so the balance can be collected.</p>`}
    <div id="dropError" style="display:none;font-size:12px;color:var(--red);margin-bottom:12px"></div>
    <div style="display:flex;gap:10px">
      <button class="btn" id="dropConfirmBtn" style="flex:1;background:#f87171" ${card ? '' : 'disabled'}>Charge &amp; complete drop</button>
      <button class="btn-ghost" style="flex:1" onclick="closeModal()">Cancel</button>
    </div>`, true);
  if (isPC) {
    document.getElementById('dropFeeSelect').addEventListener('change', () => dropUpdateTotals(childId, programId));
  } else {
    const feeInput = document.getElementById('dropFeeInput');
    feeInput.addEventListener('focus', () => currencyFocus(feeInput));
    feeInput.addEventListener('blur', () => { currencyBlur(feeInput); dropUpdateTotals(childId, programId); });
    feeInput.addEventListener('input', () => dropUpdateTotals(childId, programId));
  }
  document.getElementById('dropConfirmBtn').addEventListener('click', () => processDrop(childId, programId));
}

function dropGetFee(programId) {
  const prog = PROGRAMS.find(x => x.id === programId);
  const isPC = prog && prog.name && prog.name.toLowerCase().includes('performance company');
  if (isPC) {
    const sel = document.getElementById('dropFeeSelect');
    if (!sel || sel.value === '') return null;   // no conscious choice yet
    return parseFloat(sel.value) || 0;
  }
  // Broadway Bound: free-form amount — empty means no conscious choice yet; $0 waives.
  const inp = document.getElementById('dropFeeInput');
  if (!inp || inp.value.trim() === '') return null;
  return parseFloat((inp.value || '').replace(/[^0-9.]/g, '')) || 0;
}
function dropUpdateTotals(childId, programId) {
  const ch = DB.children.find(c => c.id === childId);
  if (!ch) return;
  const existingDue = DB.charges.filter(c => c.accountId === ch.accountId && c.status === 'due').reduce((s, c) => s + c.amount, 0);
  const fee = dropGetFee(programId);
  const feeEl = document.getElementById('dropFeeAmt');
  const totEl = document.getElementById('dropTotalAmt');
  if (feeEl) feeEl.textContent = fee === null ? '—' : fmt$(fee);
  if (totEl) totEl.textContent = fmt$(existingDue + (fee || 0));
}

async function processDrop(childId, programId) {
  const ch = DB.children.find(c => c.id === childId);
  const p = PROGRAMS.find(x => x.id === programId);
  if (!ch || !p) return;
  const fee = dropGetFee(programId);
  const errEl = document.getElementById('dropError');
  if (fee === null) {
    if (errEl) { errEl.textContent = 'Please select a cancellation fee before completing the drop.'; errEl.style.display = 'block'; }
    return;
  }
  const btn = document.getElementById('dropConfirmBtn');
  if (btn) { btn.disabled = true; btn.style.opacity = '0.7'; btn.style.cursor = 'wait'; btn.textContent = 'Processing payment…'; }
  if (errEl) errEl.style.display = 'none';

  const se = DB.seasonEnrollments.find(e => e.performerId === ch.id && e.programId === programId && (e.status === 'registered' || e.status === 'active'));
  if (!se) { toast('Enrollment not found'); return; }

  try {
    const result = await apiFetch('/enrollments/' + se.id + '/drop', {
      method: 'POST', body: JSON.stringify({ cancellationFee: fee })
    });
    ch.programIds = (ch.programIds || []).filter(id => id !== programId);
    se.status = 'dropped';
    DB.charges = await apiFetch('/charges');
    closeModal(); refresh();
    toast(`${ch.name} dropped from ${p.name} — balance paid in full`);
  } catch (e) {
    if (btn) { btn.disabled = false; btn.style.opacity = '1'; btn.style.cursor = 'pointer'; btn.textContent = 'Charge & complete drop'; }
    if (errEl) { errEl.textContent = e.message || 'Payment failed. The drop was not completed.'; errEl.style.display = 'block'; }
  }
}
